import React, { useState, useCallback } from "react";
import { CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import PhoneInput from "../../PhoneInput";
import { FormErrorContainer } from "../../FormErrorContainer";
import {
  ContentContainer,
  ModalTitle,
  ModalSubtitle,
  SectionDivider,
  SectionLabel,
  FieldContainer,
  FieldLabel,
  RequiredMark,
  StyledInput,
  PhoneInputWrapper,
  ButtonsContainer,
  CancelButton,
  SubmitButton,
} from "./EditTenantModal.styled";
import {
  tenantsService,
  UpdateTenantParams,
  Tenant,
} from "../../../services/tenantsService";
import { validateUSPhoneNumber, cleanPhoneNumber } from "../../../utils/phoneValidation";
import { validateName } from "../../../utils/nameValidation";
import { validateEmail } from "../../../utils/emailValidation";
import { validateClinicName } from "../../../utils/clinicNameValidation";

interface EditTenantFormData {
  clinicName: string;
  ownerFirstName: string;
  ownerLastName: string;
  email: string;
  phoneNumber: string;
}

interface FormErrors {
  clinicName?: string;
  ownerFirstName?: string;
  ownerLastName?: string;
  email?: string;
  phoneNumber?: string;
}

interface EditTenantModalContentProps {
  tenant: Tenant;
  onSuccess: (updated: Tenant) => void;
  onClose: () => void;
}

const EditTenantModalContent: React.FC<EditTenantModalContentProps> = ({
  tenant,
  onSuccess,
  onClose,
}) => {
  const { t } = useTranslation();
  const [initialFirstName = "", ...initialLastNameParts] = tenant.contactName.split(" ");
  const initialLastName = initialLastNameParts.join(" ");
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<EditTenantFormData>({
    clinicName: tenant.clinicName,
    ownerFirstName: initialFirstName,
    ownerLastName: initialLastName,
    email: tenant.contactEmail,
    phoneNumber: tenant.phoneNumber,
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [apiError, setApiError] = useState<string | null>(null);

  const getClinicNameError = useCallback((clinicName: string): string | null => {
    const issue = validateClinicName(clinicName);
    if (issue === "required") {
      return t(
        "tenantManagement.addTenant.errors.clinicNameRequired",
        "Clinic name is required",
      );
    }
    if (issue === "tooShort") {
      return t(
        "tenantManagement.addTenant.errors.clinicNameTooShort",
        "Clinic name must be at least 2 characters",
      );
    }
    if (issue === "tooLong") {
      return t(
        "tenantManagement.addTenant.errors.clinicNameTooLong",
        "Clinic name can not be longer than 50 characters",
      );
    }
    if (issue === "onlyNumbers") {
      return t(
        "tenantManagement.addTenant.errors.clinicNameOnlyNumbers",
        "Clinic name can not contain only numbers",
      );
    }
    if (issue === "invalidChars") {
      return t(
        "tenantManagement.addTenant.errors.clinicNameInvalidChars",
        "Clinic name contains invalid characters",
      );
    }
    return null;
  }, [t]);

  const validatePersonFirstName = useCallback((firstName: string): string | null => {
    const issue = validateName(firstName);
    if (issue === "required") {
      return t(
        "tenantManagement.addTenant.errors.firstNameRequired",
        "First name is required",
      );
    }
    if (issue === "tooLong") {
      return t(
        "tenantManagement.addTenant.errors.firstNameTooLong",
        "First name can not be longer than 50 characters.",
      );
    }
    if (issue === "invalid") {
      return t(
        "tenantManagement.addTenant.errors.firstNameInvalid",
        "Please enter a valid name.",
      );
    }
    return null;
  }, [t]);

  const validatePersonLastName = useCallback((lastName: string): string | null => {
    const issue = validateName(lastName);
    if (issue === "required") {
      return t(
        "tenantManagement.addTenant.errors.lastNameRequired",
        "Last name is required",
      );
    }
    if (issue === "tooLong") {
      return t(
        "tenantManagement.addTenant.errors.lastNameTooLong",
        "Last name can not be longer than 50 characters.",
      );
    }
    if (issue === "invalid") {
      return t(
        "tenantManagement.addTenant.errors.lastNameInvalid",
        "Please enter a valid name, using only letters.",
      );
    }
    return null;
  }, [t]);

  const validate = useCallback((): boolean => {
    const newErrors: FormErrors = {};

    const clinicNameError = getClinicNameError(formData.clinicName);
    if (clinicNameError) newErrors.clinicName = clinicNameError;

    const firstNameError = validatePersonFirstName(formData.ownerFirstName);
    if (firstNameError) newErrors.ownerFirstName = firstNameError;

    const lastNameError = validatePersonLastName(formData.ownerLastName);
    if (lastNameError) newErrors.ownerLastName = lastNameError;

    if (!formData.email.trim()) {
      newErrors.email = t(
        "tenantManagement.addTenant.errors.contactEmailRequired",
        "Contact email is required",
      );
    } else if (!validateEmail(formData.email)) {
      newErrors.email = t(
        "tenantManagement.addTenant.errors.contactEmailInvalid",
        "Enter a valid email address",
      );
    }

    if (!formData.phoneNumber) {
      newErrors.phoneNumber = t(
        "tenantManagement.addTenant.errors.phoneRequired",
        "Phone number is required",
      );
    } else if (!validateUSPhoneNumber(formData.phoneNumber)) {
      newErrors.phoneNumber = t(
        "tenantManagement.addTenant.errors.phoneInvalid",
        "Enter a valid phone number",
      );
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, t, getClinicNameError, validatePersonFirstName, validatePersonLastName]);

  const handleInputChange =
    (field: keyof EditTenantFormData) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFormData((prev) => ({ ...prev, [field]: e.target.value }));
      if (errors[field as keyof FormErrors]) {
        setErrors((prev) => ({ ...prev, [field]: undefined }));
      }
      if (apiError) setApiError(null);
    };

  const handleTrimOnBlur = (field: keyof EditTenantFormData) => () => {
    if (
      field !== "clinicName" &&
      field !== "ownerFirstName" &&
      field !== "ownerLastName" &&
      field !== "email"
    ) {
      return;
    }
    setFormData((prev) => ({ ...prev, [field]: prev[field].trim() }));
  };

  const handleSubmit = useCallback(async () => {
    setApiError(null);
    if (!validate()) return;

    setLoading(true);
    try {
      const params: UpdateTenantParams = {
        id: tenant.id,
        clinicName: formData.clinicName.trim(),
        contactName: `${formData.ownerFirstName.trim()} ${formData.ownerLastName.trim()}`.trim(),
        contactEmail: formData.email.trim(),
        phoneNumber: cleanPhoneNumber(formData.phoneNumber),
      };

      const updated = await tenantsService.updateTenant(params);
      onSuccess(updated);
    } catch (error: unknown) {
      const axiosError = error as {
        response?: {
          data?: {
            detail?: string;
            errors?: Record<string, string[]>;
          };
        };
      };

      const beFieldErrors = axiosError?.response?.data?.errors;

      if (beFieldErrors && typeof beFieldErrors === "object") {
        // BE uses PascalCase field names; the edit form combines first+last into contactName
        const fieldMapping: Record<string, keyof FormErrors> = {
          ClinicName: "clinicName",
          Email: "email",
          PhoneNumber: "phoneNumber",
          OwnerFirstName: "ownerFirstName",
          OwnerLastName: "ownerLastName",
        };

        const mapped: FormErrors = {};
        Object.entries(beFieldErrors).forEach(([key, messages]) => {
          const formField = fieldMapping[key];
          if (formField && messages.length > 0) {
            mapped[formField] = messages.join(" ");
          }
        });

        if (Object.keys(mapped).length > 0) {
          setErrors((prev) => ({ ...prev, ...mapped }));
        } else {
          setApiError(
            axiosError.response?.data?.detail ||
              t("tenantManagement.editTenant.errors.updateFailed"),
          );
        }
      } else {
        setApiError(
          axiosError?.response?.data?.detail ||
            t("tenantManagement.editTenant.errors.updateFailed"),
        );
      }
    } finally {
      setLoading(false);
    }
  }, [formData, validate, tenant.id, onSuccess, t]);

  const allErrors: string[] = [
    errors.clinicName,
    errors.ownerFirstName,
    errors.ownerLastName,
    errors.email,
    errors.phoneNumber,
    apiError ?? undefined,
  ].filter((e): e is string => Boolean(e));

  return (
    <ContentContainer>
      <ModalTitle>
        {t("tenantManagement.editTenant.title", "Edit Tenant")}
      </ModalTitle>
      <ModalSubtitle>
        {t("tenantManagement.editTenant.subtitle", "Editing")}{" "}
        {tenant.clinicName}
      </ModalSubtitle>

      <FieldContainer>
        <FieldLabel>
          {t("tenantManagement.addTenant.tenantId", "Tenant ID")}
        </FieldLabel>
        <StyledInput value={tenant.id} disabled readOnly />
      </FieldContainer>

      <FieldContainer>
        <FieldLabel>
          {t("tenantManagement.addTenant.clinicName", "Clinic Name")}
          <RequiredMark> *</RequiredMark>
        </FieldLabel>
        <StyledInput
          maxLength={50}
          value={formData.clinicName}
          onChange={handleInputChange("clinicName")}
          onBlur={handleTrimOnBlur("clinicName")}
          hasError={!!errors.clinicName}
        />
      </FieldContainer>

      <SectionDivider />
      <SectionLabel>
        {t("tenantManagement.addTenant.contactInfoSection", "Contact Information")}
      </SectionLabel>

      <FieldContainer>
        <FieldLabel>
          {t("tenantManagement.addTenant.ownerFirstName", "First Name")}
          <RequiredMark> *</RequiredMark>
        </FieldLabel>
        <StyledInput
          placeholder={t(
            "tenantManagement.addTenant.ownerFirstNamePlaceholder",
            "e.g. Jane",
          )}
          value={formData.ownerFirstName}
          onChange={handleInputChange("ownerFirstName")}
          onBlur={handleTrimOnBlur("ownerFirstName")}
          hasError={!!errors.ownerFirstName}
        />
      </FieldContainer>

      <FieldContainer>
        <FieldLabel>
          {t("tenantManagement.addTenant.ownerLastName", "Last Name")}
          <RequiredMark> *</RequiredMark>
        </FieldLabel>
        <StyledInput
          placeholder={t(
            "tenantManagement.addTenant.ownerLastNamePlaceholder",
            "e.g. Smith",
          )}
          value={formData.ownerLastName}
          onChange={handleInputChange("ownerLastName")}
          onBlur={handleTrimOnBlur("ownerLastName")}
          hasError={!!errors.ownerLastName}
        />
      </FieldContainer>

      <FieldContainer>
        <FieldLabel>
          {t("tenantManagement.addTenant.contactEmail", "Main Contact Person Email")}
          <RequiredMark> *</RequiredMark>
        </FieldLabel>
        <StyledInput
          type="email"
          placeholder={t(
            "tenantManagement.addTenant.contactEmailPlaceholder",
            "e.g. admin@clinic.com",
          )}
          value={formData.email}
          onChange={handleInputChange("email")}
          onBlur={handleTrimOnBlur("email")}
          hasError={!!errors.email}
        />
      </FieldContainer>

      <FieldContainer>
        <FieldLabel>
          {t("tenantManagement.addTenant.phoneNumber", "Phone Number")}
          <RequiredMark> *</RequiredMark>
        </FieldLabel>
        <PhoneInputWrapper>
          <PhoneInput
            name="phoneNumber"
            value={formData.phoneNumber}
            onChange={handleInputChange("phoneNumber")}
            placeholder="+1 (___) ___-____"
            error={!!errors.phoneNumber}
          />
        </PhoneInputWrapper>
      </FieldContainer>

      {allErrors.length > 0 && (
        <FormErrorContainer errors={allErrors} style={{ marginTop: 8 }} />
      )}

      <ButtonsContainer>
        <CancelButton variant="outlined" onClick={onClose} disabled={loading}>
          {t("common.cancel", "Cancel")}
        </CancelButton>
        <SubmitButton variant="contained" onClick={handleSubmit} disabled={loading}>
          {loading ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            t("tenantManagement.editTenant.submit", "Save Changes")
          )}
        </SubmitButton>
      </ButtonsContainer>
    </ContentContainer>
  );
};

export { EditTenantModalContent };
export default EditTenantModalContent;
